import { DEFAULT_GEMINI_MODEL } from "@atriam/contracts/ai";

/**
 * The model registry: every model Atriam may call, with its provider and price.
 *
 * Deliberately dependency-free and separate from cost.ts. Both the pricing
 * logic AND lib/env/server.ts validate against this table, and env must not
 * depend on a module that tests routinely mock — when the registry lived in
 * cost.ts, any suite mocking `../cost` broke env's import at collection time.
 * Data here, logic there.
 */
type ModelProvider = "openai" | "google";

export type ModelSpec = {
  provider: ModelProvider;
  inputPerMillion: number;
  outputPerMillion: number;
  cachedInputPerMillion?: number;
};

/**
 * Every model Atriam may call, keyed by the model id ALONE.
 *
 * The provider lives on the row rather than in the key on purpose. This table
 * used to be keyed `"<provider>:<model>"` and callers passed the two halves
 * separately, so they could disagree — and they did: step-runner, migration
 * inference/triage and the comms agent loop all pass provider:"openai" with a
 * gemini model, because @/lib/ai routes on the model id and ignores the field.
 * That produced an `openai:<a gemini flash model>` key, which matched no row,
 * so those calls billed at FALLBACK_PRICING ($15/$75 against a real
 * $0.30/$2.50 — 50x input, 30x output). The daily agent-cost guard summed
 * those phantom charges, so it tripped on money nobody spent and terminally
 * killed 543 bank-transaction runs on 2026-07-30.
 *
 * A model id now carries its own provider, so there is no longer a pair that
 * can disagree. Adding a model means adding one row here; nothing else needs
 * to know a provider.
 */
export const MODELS = {
  // OpenAI
  "gpt-4o": {
    provider: "openai",
    inputPerMillion: 2.5,
    outputPerMillion: 10.0,
  },
  "gpt-4o-mini": {
    provider: "openai",
    inputPerMillion: 0.15,
    outputPerMillion: 0.6,
  },
  "gpt-5.4": {
    provider: "openai",
    inputPerMillion: 5.0,
    outputPerMillion: 20.0,
  },
  // Standard tier per developers.openai.com/api/docs/pricing, 2026-09-27. The
  // old $0.30/$1.20 row under-counted the hiring analyzers and contractor research.
  "gpt-5.4-mini": {
    provider: "openai",
    inputPerMillion: 0.75,
    outputPerMillion: 4.5,
    cachedInputPerMillion: 0.075,
  },
  // gpt-5-mini pricing per OpenAI public price sheet as of 2026-05-28.
  // Update when OpenAI publishes new pricing.
  "gpt-5-mini": {
    provider: "openai",
    inputPerMillion: 0.25,
    outputPerMillion: 2.0,
    cachedInputPerMillion: 0.025,
  },
  "text-embedding-3-small": {
    provider: "openai",
    inputPerMillion: 0.02,
    outputPerMillion: 0,
  },
  "text-embedding-3-large": {
    provider: "openai",
    inputPerMillion: 0.13,
    outputPerMillion: 0,
  },
  // Google (Vertex). Thinking tokens are folded into outputTokens by the
  // client and billed at the output rate.
  //
  // Historical pricing remains available for old usage. Runtime pins retire separately.
  "gemini-3.6-flash": {
    provider: "google",
    inputPerMillion: 1.5,
    outputPerMillion: 7.5,
  },
  // Standard post-introductory rates. cost.ts selects the usage-date promotion.
  [DEFAULT_GEMINI_MODEL]: {
    provider: "google",
    inputPerMillion: 1.5,
    outputPerMillion: 7.5,
    cachedInputPerMillion: 0.15,
  },
  // Global standard rates, verified 2026-10-22. Text/image/video/audio share
  // one input rate, so aggregate provider usage also prices media correctly.
  // https://cloud.google.com/vertex-ai/generative-ai/pricing
  "gemini-3.5-flash-lite": {
    provider: "google",
    inputPerMillion: 0.3,
    outputPerMillion: 2.5,
    cachedInputPerMillion: 0.03,
  },
  "gemini-embedding-001": {
    provider: "google",
    inputPerMillion: 0.15,
    outputPerMillion: 0,
  },
  // Billed per input rather than per token; the embed sink only reports a count
  // when Vertex returns one, so this row exists to avoid the opus fallback
  // rather than to price it precisely.
  "multimodalembedding@001": {
    provider: "google",
    inputPerMillion: 0.15,
    outputPerMillion: 0,
  },
} as const satisfies Record<string, ModelSpec>;

type ModelId = keyof typeof MODELS;

/** True when the id has a real pricing row (no opus-grade fallback needed). */
export function isKnownModel(model: string): model is ModelId {
  return Object.hasOwn(MODELS, model);
}

/** Sorted ids, for error messages that tell an operator what IS valid. */
export function knownModelIds(): string[] {
  return Object.keys(MODELS).sort();
}
